import {useEffect} from 'react';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getRoot,$getNodeByKey,$isTextNode,$isParagraphNode,$createParagraphNode,$getSelection,$isRangeSelection,KEY_BACKSPACE_COMMAND,KEY_DELETE_COMMAND,COMMAND_PRIORITY_HIGH} from 'lexical';
import {$isHeadingNode,$createHeadingNode} from '@lexical/rich-text';
import {$isListNode,$createListNode} from '@lexical/list';
import {$isTableNode,$createTableNode} from '@lexical/table';
import {$createPageNode} from '../nodes/PageNode.jsx';
import {A4_HEIGHT_PX,PAGE_BOTTOM_MM} from '../constants';
const TAG='doc-pagination';
const empty=n=>n&&$isParagraphNode(n)&&!n.getTextContent().trim();
const num=v=>{const n=parseFloat(v);return Number.isFinite(n)?n:0;};
// Printable bottom edge of a page, read from the real CSS so it cannot drift from DocEditor.css.
function limitOf(el){
 const cs=getComputedStyle(el),min=num(cs.minHeight)||A4_HEIGHT_PX,pad=num(cs.paddingBottom)||PAGE_BOTTOM_MM*96/25.4;
 return el.getBoundingClientRect().top+min-pad;
}
function caretInside(node){const s=$getSelection();return $isRangeSelection(s)&&node.isParentOf(s.anchor.getNode());}
// An empty shell of the same block type (version-independent replacement for $copyNode).
function shellOf(node){
 if($isHeadingNode(node)){const n=$createHeadingNode(node.getTag());n.setFormat(node.getFormatType());n.setIndent(node.getIndent());return n;}
 if($isListNode(node))return $createListNode(node.getListType());
 if($isTableNode(node))return $createTableNode();
 const n=$createParagraphNode();n.setFormat(node.getFormatType());n.setIndent(node.getIndent());return n;
}
function plan(editor){
 return editor.getEditorState().read(()=>{
  const pages=$getRoot().getChildren();
  if(pages.length>1)for(const page of pages){
   const kids=page.getChildren();
   if((kids.length===0||kids.every(empty))&&!caretInside(page))return {type:'remove',key:page.getKey()};
  }
  for(let i=0;i<pages.length;i++){
   const page=pages[i],el=editor.getElementByKey(page.getKey());if(!el)continue;
   const bottom=limitOf(el);
   for(const child of page.getChildren()){
    const dom=editor.getElementByKey(child.getKey());if(!dom||dom.getBoundingClientRect().bottom<=bottom+0.5)continue;
    if(($isParagraphNode(child)||$isHeadingNode(child))&&child.getChildren().length&&child.getChildren().every($isTextNode)){
     let count=0,fit=0;
     for(const tn of child.getChildren()){
      const te=editor.getElementByKey(tn.getKey());if(!te)break;
      const walker=document.createTreeWalker(te,NodeFilter.SHOW_TEXT);let text;
      while((text=walker.nextNode()))for(let c=0;c<text.length;c++){
       const r=document.createRange();r.setStart(text,c);r.setEnd(text,c+1);if(r.getBoundingClientRect().bottom<=bottom-1)fit=count+1;count++;
      }
     }
     if(fit>0&&fit<child.getTextContentSize()){
      for(const m of child.getTextContent().matchAll(/\{\{[^{}]*\}\}/g))if(fit>m.index&&fit<m.index+m[0].length)fit=m.index;
      if(fit>0)return {type:'text',key:child.getKey(),offset:fit,page:page.getKey()};
     }
    }
    if($isListNode(child)||$isTableNode(child)){
     const j=child.getChildren().findIndex(p=>{const d=editor.getElementByKey(p.getKey());return d&&d.getBoundingClientRect().bottom>bottom+0.5;});
     if(j>0)return {type:'container',key:child.getKey(),offset:j,page:page.getKey()};
    }
    if(child.getIndexWithinParent()>0)return {type:'move',key:child.getKey(),page:page.getKey()};
    return {type:'oversize',key:child.getKey()};
   }
   const next=pages[i+1];if(!next)continue;
   const first=next.getFirstChild(),fd=first&&editor.getElementByKey(first.getKey()),last=page.getLastChild(),ld=last&&editor.getElementByKey(last.getKey());
   if(fd&&ld&&fd.getBoundingClientRect().height+ld.getBoundingClientRect().bottom<bottom-3)return {type:'pull',key:first.getKey(),page:page.getKey()};
  }
  return null;
 });
}
function apply(p){
 const node=$getNodeByKey(p.key);if(!node)return;
 if(p.type==='remove'){node.remove();return;}
 const page=$getNodeByKey(p.page);if(!page)return;
 if(p.type==='pull'){
  const from=node.getParent();page.append(node);
  if(from&&from.getChildrenSize()===0)from.remove(); // an empty section would print as a blank PDF page
  return;
 }
 let next=page.getNextSibling();if(!next){next=$createPageNode();page.insertAfter(next);}
 let moved=node;
 const trailing=node.getNextSiblings(); // blocks after the split block always travel with it
 if(p.type==='container'){moved=shellOf(node);node.getChildren().slice(p.offset).forEach(c=>moved.append(c));}
 else if(p.type==='text'){
  moved=shellOf(node);let offset=p.offset;
  node.getChildren().forEach(t=>{const length=t.getTextContentSize();if(offset>=length){offset-=length;return;}
   if(offset>0){const parts=t.splitText(offset);moved.append(parts[1]);offset=0;}else moved.append(t);
  });
 }
 const first=next.getFirstChild();
 if(first){first.insertBefore(moved);trailing.forEach(t=>first.insertBefore(t));}else next.append(moved,...trailing);
}
function scrollCaret(editor){
 const root=editor.getRootElement(),sel=window.getSelection();
 if(!root||!sel||!sel.rangeCount||!root.contains(sel.anchorNode))return;
 const n=sel.anchorNode,el=n.nodeType===1?n:n.parentElement;
 el?.scrollIntoView({block:'nearest',inline:'nearest'});
}
export default function PaginationPlugin({onIssue}){
 const [editor]=useLexicalComposerContext();
 useEffect(()=>{
  let raf=0,steps=0,disposed=false,changed=false;
  const run=()=>{
   if(disposed||editor.isComposing())return;
   const p=plan(editor);
   if(!p){steps=0;if(changed){changed=false;scrollCaret(editor);}return;}
   if(p.type==='oversize'){onIssue('One block is larger than the printable page. Shorten the table row or locked block, or reduce its font/image size. Nothing has been deleted or hidden.');return;}
   if(++steps>300){steps=0;onIssue('Pagination stopped to avoid a reflow loop. Please inspect oversized blocks before exporting.');return;}
   changed=true;
   editor.update(()=>apply(p),{tag:[TAG,'history-merge'],discrete:true});
   raf=requestAnimationFrame(run);
  };
  const schedule=()=>{cancelAnimationFrame(raf);raf=requestAnimationFrame(run);};
  const off=editor.registerUpdateListener(({tags})=>{if(!tags.has(TAG)){steps=0;schedule();}});
  const root=editor.getRootElement();root?.addEventListener('load',schedule,true);root?.addEventListener('compositionend',schedule);
  document.fonts?.ready.then(()=>{if(!disposed)schedule();});
  const ro=new ResizeObserver(schedule);if(root)ro.observe(root);
  const offs=[KEY_BACKSPACE_COMMAND,KEY_DELETE_COMMAND].map((command,index)=>editor.registerCommand(command,event=>{
   const s=$getSelection();if(!$isRangeSelection(s)||!s.isCollapsed())return false;
   const n=s.anchor.getNode();let block=n;
   while(block.getParent()&&block.getParent().getType()!=='doc-page')block=block.getParent();
   const page=block.getParent();if(!page||page.getType()!=='doc-page')return false;
   if(index===0&&block.is(page.getFirstChild())&&s.anchor.offset===0){const prev=page.getPreviousSibling();if(prev){event?.preventDefault();prev.selectEnd();return true;}}
   if(index===1&&block.is(page.getLastChild())&&s.anchor.offset===n.getTextContentSize()){const next=page.getNextSibling();if(next){event?.preventDefault();next.selectStart();return true;}}
   return false;
  },COMMAND_PRIORITY_HIGH));
  schedule();
  return()=>{disposed=true;off();offs.forEach(o=>o());ro.disconnect();root?.removeEventListener('load',schedule,true);root?.removeEventListener('compositionend',schedule);cancelAnimationFrame(raf);};
 },[editor,onIssue]);
 return null;
}
